(() => {
  const initialize = () => {
    if (!document.body.classList.contains('mn-premium') || window.Shopify?.designMode || window.__mnProductCardMotion) return;
    if (!('IntersectionObserver' in window) || !('MutationObserver' in window) || typeof Element.prototype.animate !== 'function') return;

    window.__mnProductCardMotion = true;

    const selector = '.mn-product-card__visual';
    const pendingClass = 'mn-product-card__visual--pending';
    const loadingClass = 'mn-product-card__media--loading';
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pending = new Map();
    const observed = new Map();
    const thresholds = [0, 0.001, 0.01, 0.05, 0.1, 0.18];
    let suspended = false;

    const sizeLazyImage = (image) => {
      if (image.loading !== 'lazy' || image.currentSrc || !image.getAttribute('srcset')) return;
      const sizes = image.getAttribute('sizes');
      if (/^\s*auto(?:\s*,|$)/.test(sizes || '')) return;
      image.setAttribute('sizes', sizes ? `auto, ${sizes}` : 'auto');
    };

    const stop = (state) => {
      const animation = state.animation;
      state.animation = null;
      state.paused = false;
      if (animation) animation.cancel();
    };

    const settle = (state) => {
      state.armed = false;
      state.visual.classList.remove(pendingClass);
      stop(state);
    };

    const syncLoading = (state) => {
      state.media.classList.toggle(loadingClass, !suspended && !preference.matches && state.visible && !state.errored && !state.image?.complete);
    };

    const dispose = (state) => {
      if (pending.get(state.visual) !== state) return;
      observer.unobserve(state.media);
      proximityObserver.unobserve(state.media);
      observed.delete(state.media);
      state.image?.removeEventListener('load', state.onLoad);
      state.image?.removeEventListener('error', state.onError);
      pending.delete(state.visual);
      state.generation += 1;
      state.visual.classList.remove(pendingClass);
      state.media.classList.remove(loadingClass);
      stop(state);
    };

    const play = (state) => {
      if (suspended || window.__mnPageMotion?.active || pending.get(state.visual) !== state || !state.armed || !state.visible || !state.qualified || !state.ready) return;
      if (preference.matches || !state.visual.isConnected) {
        if (!state.visual.isConnected) dispose(state);
        return;
      }

      state.armed = false;
      state.seen = true;

      try {
        const animation = state.visual.animate(
          [{ transform: 'scale(1.08)' }, { transform: 'scale(1)' }],
          { duration: 1250, easing: 'cubic-bezier(.22, .7, .3, 1)', fill: 'forwards' }
        );
        state.animation = animation;
        const complete = () => {
          if (state.animation !== animation) return;
          state.animation = null;
          state.paused = false;
          state.visual.classList.remove(pendingClass);
          animation.cancel();
        };
        animation.finished.then(complete, complete);
      } catch {
        settle(state);
      }
    };

    const prepare = (state) => {
      if (suspended || pending.get(state.visual) !== state || !state.armed || (!state.near && !state.visible) || state.errored || preference.matches) return;
      if (!state.image?.complete || !state.image.naturalWidth) return;

      if (state.ready) {
        play(state);
        return;
      }
      if (state.decoding) return;

      state.decoding = true;
      const generation = state.generation;
      const image = state.image;
      let decoded;
      try {
        decoded = typeof image.decode === 'function' ? image.decode() : undefined;
      } catch {
        decoded = undefined;
      }

      Promise.resolve(decoded).catch(() => {}).then(() => {
        if (suspended || pending.get(state.visual) !== state || generation !== state.generation || image !== state.image) return;
        state.decoding = false;
        if (!image.complete || !image.naturalWidth) return;
        state.ready = true;
        play(state);
      });
    };

    const refreshImage = (state) => {
      const image = state.visual.querySelector('.product-card__image--primary');
      if (image === state.image) return;
      stop(state);
      state.image?.removeEventListener('load', state.onLoad);
      state.image?.removeEventListener('error', state.onError);
      state.image = image;
      state.ready = false;
      state.decoding = false;
      state.errored = false;
      state.seen = state.visible;
      state.armed = !preference.matches && !state.seen;
      state.visual.classList.toggle(pendingClass, state.armed);
      state.generation += 1;
      image?.addEventListener('load', state.onLoad);
      image?.addEventListener('error', state.onError);
      if (image?.complete && !image.naturalWidth) state.onError();
      syncLoading(state);
      prepare(state);
    };

    const minimumRatio = (bounds, root) => {
      if (!(bounds?.width > 0 && bounds.height > 0)) return 0.18;
      const width = root?.width ?? document.documentElement.clientWidth;
      const height = root?.height ?? document.documentElement.clientHeight;
      const maximum = Math.min(1, width / bounds.width) * Math.min(1, height / bounds.height);
      if (maximum >= 0.18) return 0.18;
      const reachable = maximum * 0.5;
      let threshold = 0;
      for (const candidate of thresholds) {
        if (candidate > reachable) break;
        threshold = candidate;
      }
      return threshold;
    };

    const updateIntersections = (entries) => {
      for (const entry of entries) {
        const state = observed.get(entry.target);
        if (!state) continue;
        state.intersectionKnown = true;
        state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
        syncLoading(state);
        if (!state.visible) {
          state.qualified = false;
          continue;
        }
        if (preference.matches) state.seen = true;

        state.qualified = entry.intersectionRatio >= minimumRatio(entry.boundingClientRect, entry.rootBounds);
        prepare(state);
      }
    };
    const observer = new IntersectionObserver(updateIntersections, { threshold: thresholds, rootMargin: '0px' });

    // Decode nearby, already-loaded images before entry. The small outer boundary
    // also prevents a reversal at the viewport edge from snapping back to 1.08.
    const proximityObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const state = observed.get(entry.target);
        if (!state) continue;
        state.near = entry.isIntersecting;
        if (state.near) {
          prepare(state);
        } else if (state.seen && !suspended) {
          state.visible = false;
          state.qualified = false;
          state.seen = false;
          state.armed = !preference.matches && !state.errored;
          state.visual.classList.toggle(pendingClass, state.armed);
          stop(state);
          syncLoading(state);
        }
      }
    }, { threshold: [0], rootMargin: '120px 32px' });

    const register = (visual) => {
      const state = pending.get(visual);
      if (state) {
        refreshImage(state);
        return;
      }
      if (!visual.closest('product-card')) return;

      const image = visual.querySelector('.product-card__image--primary');
      const media = visual.closest('.product-card__media');
      if (!image || !media) return;
      const next = { visual, image, media, near: false, visible: false, qualified: false, intersectionKnown: false, armed: !preference.matches, seen: false, ready: false, decoding: false, errored: false, generation: 0, animation: null, paused: false };
      next.onLoad = () => {
        if (next.errored) {
          next.errored = false;
          next.seen = next.visible;
          next.armed = !preference.matches && !next.seen;
          next.visual.classList.toggle(pendingClass, next.armed);
        }
        syncLoading(next);
        prepare(next);
      };
      next.onError = () => {
        next.ready = false;
        next.decoding = false;
        next.errored = true;
        next.generation += 1;
        next.media.classList.remove(loadingClass);
        settle(next);
      };
      pending.set(visual, next);
      observed.set(media, next);
      visual.classList.toggle(pendingClass, next.armed);
      image.addEventListener('load', next.onLoad);
      image.addEventListener('error', next.onError);
      if (image.complete && !image.naturalWidth) next.onError();
      observer.observe(media);
      proximityObserver.observe(media);
    };

    const enhance = (card) => {
      const media = card.querySelector('.product-card__media');
      if (!media) return;
      const images = Array.from(media.children).filter((node) => node.matches('img.product-card__image'));
      let visual = media.querySelector(selector);
      if (!visual && images.length) {
        visual = document.createElement('span');
        visual.className = `mn-product-card__visual ${pendingClass}`;
        media.insertBefore(visual, images[0]);
      }
      if (!visual) return;
      for (const image of images) {
        if (image.classList.contains('product-card__image--primary')) visual.insertBefore(image, visual.firstChild);
        else visual.appendChild(image);
      }
      for (const image of visual.querySelectorAll('img.product-card__image')) sizeLazyImage(image);
      register(visual);
    };

    const collect = (root, cards, visuals) => {
      if (root.nodeType !== 1 || root.matches?.('script, style, link')) return;
      const containingCard = root.closest('product-card');
      if (containingCard) cards.add(containingCard);
      for (const card of root.querySelectorAll('product-card')) cards.add(card);
      const containingVisual = root.closest(selector);
      if (containingVisual) visuals.add(containingVisual);
      for (const visual of root.querySelectorAll(selector)) visuals.add(visual);
    };

    const discover = (root) => {
      const cards = new Set();
      const visuals = new Set();
      collect(root, cards, visuals);
      for (const card of cards) enhance(card);
      for (const visual of visuals) if (!pending.has(visual)) register(visual);
    };

    const discard = (root) => {
      if (root.nodeType !== 1) return;
      const release = (visual) => {
        const state = pending.get(visual);
        if (state && !visual.isConnected) dispose(state);
      };
      if (root.matches(selector)) release(root);
      for (const visual of root.querySelectorAll(selector)) release(visual);
    };

    const mutations = new MutationObserver((records) => {
      const cards = new Set();
      const visuals = new Set();
      const changed = new Set();
      for (const record of records) {
        for (const node of record.addedNodes) collect(node, cards, visuals);
        for (const node of record.removedNodes) discard(node);
        const visual = record.target?.closest?.(selector);
        if (visual && pending.has(visual)) changed.add(visual);
      }
      for (const card of cards) if (card.isConnected) enhance(card);
      for (const visual of visuals) if (visual.isConnected && !pending.has(visual)) register(visual);
      for (const visual of changed) {
        const state = pending.get(visual);
        if (state) refreshImage(state);
      }
    });

    const updatePreference = () => {
      for (const state of pending.values()) {
        if (state.visible) state.seen = true;
        stop(state);
        state.armed = !preference.matches && !state.seen && !state.errored;
        state.visual.classList.toggle(pendingClass, state.armed);
        syncLoading(state);
      }
    };

    const primeViewport = () => {
      if (preference.matches) return;
      const records = observer.takeRecords?.();
      if (records?.length) updateIntersections(records);
      const root = document.documentElement;
      const viewport = { width: root.clientWidth, height: root.clientHeight };
      // Read the first viewport in one batch; IO continues to own ordinary scrolling.
      const measurements = [];
      for (const state of pending.values()) {
        if (!state.armed || state.seen || state.intersectionKnown || !state.media.isConnected) continue;
        // Carousel clipping is owned by IO, so unseen slides keep their entry for the swipe.
        if (state.media.closest('scroll-carousel')) continue;
        const bounds = state.media.getBoundingClientRect();
        const width = Math.max(0, Math.min(bounds.right, viewport.width) - Math.max(bounds.left, 0));
        const height = Math.max(0, Math.min(bounds.bottom, viewport.height) - Math.max(bounds.top, 0));
        const ratio = bounds.width > 0 && bounds.height > 0 ? width * height / (bounds.width * bounds.height) : 0;
        measurements.push({ state, visible: ratio > 0, qualified: ratio > 0 && ratio >= minimumRatio(bounds, viewport) });
      }
      for (const measurement of measurements) {
        measurement.state.visible = measurement.visible;
        measurement.state.qualified = measurement.qualified;
      }
    };

    const pageReady = () => {
      if (suspended || window.__mnPageMotion?.active) return;
      primeViewport();
      for (const state of pending.values()) {
        if (state.animation && state.paused) {
          state.paused = false;
          state.animation.play();
        }
        syncLoading(state);
        prepare(state);
      }
    };

    document.addEventListener('mn:page-start', () => {
      for (const state of pending.values()) {
        if (state.animation && !state.paused) {
          state.animation.pause();
          state.paused = true;
        }
      }
    });
    document.addEventListener('mn:page-ready', pageReady);
    window.addEventListener('pagehide', () => {
      observer.takeRecords?.();
      proximityObserver.takeRecords?.();
      suspended = true;
      for (const state of pending.values()) {
        state.generation += 1;
        state.decoding = false;
        if (state.animation || state.visible) {
          state.seen = true;
          settle(state);
        }
        state.media.classList.remove(loadingClass);
      }
    });
    window.addEventListener('pageshow', () => {
      if (suspended) {
        observer.takeRecords?.();
        proximityObserver.takeRecords?.();
        for (const state of pending.values()) {
          observer.unobserve(state.media);
          observer.observe(state.media);
          proximityObserver.unobserve(state.media);
          proximityObserver.observe(state.media);
        }
      }
      suspended = false;
      for (const state of pending.values()) syncLoading(state);
      pageReady();
    });
    if (typeof preference.addEventListener === 'function') preference.addEventListener('change', updatePreference);
    else if (typeof preference.addListener === 'function') preference.addListener(updatePreference);

    discover(document.body);
    pageReady();
    mutations.observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) initialize();
  else document.addEventListener('DOMContentLoaded', initialize, { once: true });
})();



(() => {
  if (!('customElements' in window) || customElements.get('mn-collection-filters')) return;

  customElements.define('mn-collection-filters', class extends HTMLElement {
    connectedCallback() {
      this.launcher = this.querySelector('.mn-filters-launcher');
      if (!this.launcher) return;
      this.pendingOpen = false;
      this.onClick = () => {
        this.pendingOpen = true;
        this.launcher.setAttribute('aria-label', this.launcher.dataset.loadingLabel);
        this.sync();
      };
      this.launcher.addEventListener('click', this.onClick);
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this, { childList: true, subtree: true });
      this.sync();
    }

    sync() {
      const currentLink = this.querySelector('.cloud-search-page-controls__filters-link');
      if (!currentLink) return;
      if (!this.pendingOpen) {
        if (document.activeElement === this.launcher) currentLink.focus();
        return;
      }
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const link = this.querySelector('.cloud-search-page-controls__filters-link');
        if (!this.isConnected || !link?.isConnected) return;
        this.pendingOpen = false;
        if (document.activeElement === this.launcher) link.focus();
        link.click();
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.launcher?.removeEventListener('click', this.onClick);
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
})();

(() => {
  const initialize = () => {
    if (!document.body.classList.contains('mn-premium') || window.__mnFilterAccessibility) return;
    if (!('MutationObserver' in window)) return;
    window.__mnFilterAccessibility = true;

    const initialized = new Set();
    const hosts = new Map();
    const background = new Map();
    const groups = '.cloud-search-filters-sidebar .cloud-search-filter[data-filter-name], .cloud-search-filters-pane .cloud-search-filter[data-filter-name]';
    const launchers = '.cloud-search-page-controls__filters-link, .mn-filters-launcher';
    const focusable = 'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex], [contenteditable="true"]';
    let pane = null;
    let paneObserver = null;
    let launcher = null;
    let focusedHeading = null;
    let frame = null;
    let focusFrame = null;
    let sequence = 0;

    const visible = (element) => element?.isConnected && !element.closest('[hidden], [inert], [aria-hidden="true"]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
    const focus = (element) => {
      if (visible(element)) element.focus({ preventScroll: true });
    };
    const identify = (element, prefix) => {
      if (!element.id) element.id = `${prefix}-${++sequence}`;
      return element.id;
    };
    const nativeButton = (element) => element.matches('button, input, a[href], summary');
    const button = (element) => {
      if (!nativeButton(element)) element.setAttribute('role', 'button');
      if (element.tabIndex < 0) element.tabIndex = 0;
    };
    const controls = () => Array.from(pane.querySelectorAll(focusable)).filter((element) => element.tabIndex >= 0 && !element.disabled && element.getAttribute('aria-disabled') !== 'true' && visible(element));
    const paneFocus = () => pane?.querySelector('.cloud-search-filters-pane__heading') || pane;
    const updateBackground = () => {
      for (const element of document.body.children) {
        if (element.contains(pane) || element.matches('script, style, link')) continue;
        if (!background.has(element)) background.set(element, element.inert);
        element.inert = true;
      }
    };
    const restoreBackground = () => {
      for (const [element, inert] of background) element.inert = inert;
      background.clear();
    };

    const enhanceGroups = (root) => {
      for (const group of root.querySelectorAll(groups)) {
        const name = group.dataset.filterName;
        const heading = group.querySelector('.cloud-search-filter__name');
        if (!name || !heading?.isConnected || !heading.querySelector('.cloud-search-filter__expand, .cloud-search-filter__collapse')) continue;
        if (!initialized.has(name)) {
          initialized.add(name);
          if (heading.querySelector('.cloud-search-filter__collapse')) heading.click();
        }
        button(heading);
        heading.setAttribute('aria-expanded', String(Boolean(heading.querySelector('.cloud-search-filter__collapse'))));
        const values = group.querySelector('.cloud-search-filter__values');
        if (values) heading.setAttribute('aria-controls', identify(values, 'mn-filter-values'));
        else heading.removeAttribute('aria-controls');
      }
    };

    const sync = () => {
      frame = null;
      if (!document.body.classList.contains('mn-premium')) return;
      for (const [host, observer] of hosts) {
        if (host.isConnected) continue;
        observer.disconnect();
        hosts.delete(host);
      }
      for (const host of document.querySelectorAll('#cloud_search_filters_sidebar, #cloud_search_filters_root')) {
        if (!hosts.has(host)) {
          const observer = new MutationObserver(schedule);
          observer.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
          hosts.set(host, observer);
        }
        enhanceGroups(host);
      }

      const next = document.querySelector('.cloud-search-filters-pane');
      if (next !== pane) {
        const previous = pane;
        const active = document.activeElement;
        const restore = previous && (active === document.body || active === document.documentElement || !active?.isConnected || previous.contains(active));
        paneObserver?.disconnect();
        paneObserver = null;
        cancelAnimationFrame(focusFrame);
        restoreBackground();
        pane = next;
        if (pane) {
          paneObserver = new MutationObserver(schedule);
          paneObserver.observe(pane.parentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
          focusFrame = requestAnimationFrame(() => {
            focusFrame = null;
            if (pane === next && pane.isConnected) focus(paneFocus());
          });
        } else if (restore) {
          focusedHeading = null;
          focusFrame = requestAnimationFrame(() => {
            focusFrame = null;
            if (pane || (document.activeElement !== document.body && document.activeElement !== document.documentElement && visible(document.activeElement))) return;
            focus(visible(launcher) ? launcher : Array.from(document.querySelectorAll(launchers)).find(visible));
          });
        }
      }

      if (pane) {
        identify(pane, 'mn-filter-dialog');
        pane.setAttribute('role', 'dialog');
        pane.setAttribute('aria-modal', 'true');
        pane.tabIndex = -1;
        const title = pane.querySelector('.cloud-search-filters-pane__heading');
        if (title) {
          title.tabIndex = -1;
          pane.setAttribute('aria-labelledby', identify(title, 'mn-filter-title'));
        }
        const close = pane.querySelector('.cloud-search-filters-pane__close');
        if (close) {
          button(close);
          const nativeClose = document.querySelector('[is="dialog-close-button"][aria-label], [is="dialog-close-button"] .sr-only');
          const label = nativeClose?.getAttribute('aria-label') || nativeClose?.textContent?.trim();
          if (label) close.setAttribute('aria-label', label);
          close.querySelector('svg')?.setAttribute('aria-hidden', 'true');
        }
        enhanceGroups(pane.parentElement);
        updateBackground();
      }

      for (const trigger of document.querySelectorAll(launchers)) {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', String(Boolean(pane)));
        if (pane) trigger.setAttribute('aria-controls', pane.id);
      }
      if (focusedHeading && !focusedHeading.element.isConnected && document.activeElement === document.body) {
        const scope = focusedHeading.mobile ? pane : document;
        const replacement = Array.from(scope?.querySelectorAll(groups) || []).find((group) => group.dataset.filterName === focusedHeading.name)?.querySelector('.cloud-search-filter__name');
        if (replacement) focus(replacement);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.(launchers);
      if (trigger) launcher = trigger;
      if (trigger || event.target.closest?.('.cloud-search-filter__name, .cloud-search-filters-pane__close, .cloud-search-filters-pane-backdrop, .cloud-search-filters-apply')) schedule();
    }, true);
    document.addEventListener('keydown', (event) => {
      if (!document.body.classList.contains('mn-premium')) return;
      const heading = event.target.closest?.('.cloud-search-filter__name');
      const close = event.target.closest?.('.cloud-search-filters-pane__close');
      const trigger = event.target.closest?.(launchers);
      if ((event.key === 'Enter' || event.key === ' ') && ((heading?.closest(groups) && !nativeButton(heading)) || (close && !nativeButton(close)) || (event.key === ' ' && trigger?.matches('a[href]')))) {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) (heading || close || trigger).click();
        schedule();
        return;
      }
      if (!pane?.isConnected) return;
      if (event.key === 'Escape') {
        const dismiss = pane.querySelector('.cloud-search-filters-pane__close');
        if (!dismiss) return;
        event.preventDefault();
        event.stopPropagation();
        dismiss.click();
        schedule();
      } else if (event.key === 'Tab') {
        const items = controls();
        const active = document.activeElement;
        if (!items.length || !pane.contains(active) || active === pane || active === paneFocus() || (event.shiftKey && active === items[0]) || (!event.shiftKey && active === items.at(-1))) {
          event.preventDefault();
          focus(items.length ? (event.shiftKey ? items.at(-1) : items[0]) : paneFocus());
        }
      }
    }, true);
    document.addEventListener('focusin', (event) => {
      const heading = event.target.closest?.('.cloud-search-filter__name');
      const group = heading?.closest(groups);
      focusedHeading = group ? { element: heading, name: group.dataset.filterName, mobile: Boolean(heading.closest('.cloud-search-filters-pane')) } : null;
      if (pane?.isConnected && !pane.contains(event.target)) focus(paneFocus());
    });
    const portals = new MutationObserver(schedule);
    portals.observe(document.body, { childList: true });
    document.addEventListener('shopify:section:load', schedule);
    sync();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();

(() => {
  const initialize = () => {
    if (!document.body.classList.contains('mn-premium') || window.Shopify?.designMode || window.__mnHeadingMotion) return;
    if (!('IntersectionObserver' in window) || typeof Element.prototype.animate !== 'function') return;
    window.__mnHeadingMotion = true;

    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pendingClass = 'mn-heading-motion--pending';
    const states = new Map();
    const selector = '#main h1, #main .section-header, #main .mn-home-section-header, #main .mn-brand-directory__header, #main .mn-home-story--team .prose, #main .mn-home-story--gift .prose';
    const exclude = '.shopify-section--footer, .shopify-section--newsletter, dialog, .drawer, .modal';
    let suspended = false;

    const stop = (state) => {
      const animation = state.animation;
      state.animation = null;
      state.paused = false;
      state.element.classList.remove(pendingClass);
      animation?.cancel();
    };

    const play = (state) => {
      if (suspended || window.__mnPageMotion?.active || preference.matches || state.seen || !state.qualified) return;
      if (!state.element.isConnected) {
        stop(state);
        states.delete(state.element);
        return;
      }
      state.seen = true;
      observer.unobserve(state.element);
      try {
        const animation = state.element.animate(
          [{ transform: 'translateY(10px)' }, { transform: 'translateY(0)' }],
          { duration: 480, easing: 'cubic-bezier(.22, .7, .3, 1)', fill: 'forwards' }
        );
        state.animation = animation;
        const finish = () => { if (state.animation === animation) stop(state); };
        animation.finished.then(finish, finish);
      } catch {
        stop(state);
      }
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const state = states.get(entry.target);
        if (!state) continue;
        state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
        state.qualified = state.visible && entry.intersectionRatio >= .12;
        play(state);
      }
    }, { threshold: [0, .12], rootMargin: '0px' });

    const register = (element) => {
      if (states.has(element) || element.closest(exclude)) return;
      if (element.parentElement?.closest(selector)) return;
      const bounds = element.getBoundingClientRect();
      const root = document.documentElement;
      const visible = bounds.bottom > 0 && bounds.top < root.clientHeight && bounds.right > 0 && bounds.left < root.clientWidth;
      // First-fold headings stay in place. Only unseen headings are primed before observation.
      const state = { element, visible, qualified: false, seen: visible || preference.matches, animation: null, paused: false };
      states.set(element, state);
      if (state.seen) return;
      element.classList.add(pendingClass);
      observer.observe(element);
    };

    const scan = (root) => {
      for (const [element, state] of states) {
        if (element.isConnected) continue;
        observer.unobserve(element);
        stop(state);
        states.delete(element);
      }
      if (root.matches?.(selector)) register(root);
      for (const element of root.querySelectorAll(selector)) register(element);
    };

    const pageReady = () => {
      if (suspended || window.__mnPageMotion?.active) return;
      for (const state of states.values()) {
        if (state.animation && state.paused) {
          state.paused = false;
          state.animation.play();
        }
        play(state);
      }
    };
    document.addEventListener('mn:page-start', () => {
      for (const state of states.values()) {
        if (state.animation && !state.paused) {
          state.animation.pause();
          state.paused = true;
        }
      }
    });
    document.addEventListener('mn:page-ready', pageReady);
    window.addEventListener('pagehide', () => {
      suspended = true;
      for (const state of states.values()) {
        if (state.animation || state.visible) {
          state.seen = true;
          stop(state);
        }
      }
    });
    window.addEventListener('pageshow', () => { suspended = false; pageReady(); });
    const updatePreference = () => {
      if (!preference.matches) return;
      for (const state of states.values()) {
        state.seen = true;
        observer.unobserve(state.element);
        stop(state);
      }
    };
    if (typeof preference.addEventListener === 'function') preference.addEventListener('change', updatePreference);
    else preference.addListener?.(updatePreference);

    scan(document);
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  };
  if (document.body) initialize();
  else document.addEventListener('DOMContentLoaded', initialize, { once: true });
})();
